import { DataType, DateTime } from '@mode-switch/express';
import { Attr, Names } from '@mode-switch/model';
import { DatePart, EncField, Encoding, VariableType } from '@mode-vegas/vegas';
import { ChartRenderOptions } from '@mode/charts/domain';
import { ChartDate, RenderDataModel, makeSeriesNameFormatter } from '@mode/charts/util-vegas';
import { ChartRenderTypes, ChartTypes, HighchartsEvents } from '@mode/shared/contract-common';
import { CompiledFormula } from '@mode/shared/util-switch';
import type * as Highcharts from 'highcharts';
import * as $ from 'jquery';
import { escape, isArray, merge, trim } from 'lodash';
import * as numeral from 'numeral';

import ChartData = ChartRenderTypes.ChartData;
import SeriesType = ChartRenderTypes.SeriesType;

export type ChartFormatter = (
  model: RenderDataModel,
  encoding: Encoding,
  chartOptions: Partial<Highcharts.Options>,
  chartData: ChartData,
  chartFormat: ChartTypes.ChartFormat,
  renderOptions?: ChartRenderOptions
) => Highcharts.Options;

export namespace ChartFormatter {
  /**
   * Chart
   */

  export function formatChart(
    model: RenderDataModel,
    encoding: Encoding,
    chartOptions: Partial<Highcharts.Options>,
    chartData: ChartData,
    chartFormat: ChartTypes.ChartFormat,
    renderOptions?: ChartRenderOptions
  ): Highcharts.Options {
    return merge(chartOptions, {
      chart: {
        animation: false,
        backgroundColor: null,
        spacing: [10, 20, 20, 20],
        style: {
          fontFamily: '"Graphik", Helvetica, Arial, sans-serif',
        },
      },

      credits: {
        enabled: false,
      },
    });
  }

  /**
   * Colors
   */

  export function formatColors(
    model: RenderDataModel,
    encoding: Encoding,
    chartOptions: Partial<Highcharts.Options>,
    chartData: ChartData,
    chartFormat: ChartTypes.ChartFormat,
    renderOptions: ChartRenderOptions
  ): Highcharts.Options {
    let { palette, reportLevelColorMap } = renderOptions;
    reportLevelColorMap = reportLevelColorMap || {};

    let colors = new Array<string>();
    let nextUnassignedColorIndex = 0;
    function appendColorFor(seriesName: string) {
      let color: string;
      if (chartFormat.colors.reportLevel) {
        const i = reportLevelColorMap['$' + seriesName];
        color = palette[i % palette.length]; // have to modulo by palette.length in case colors have been removed from palette since color map was created
      } else {
        color = chartFormat.colors.map[seriesName];
      }
      if (color) {
        colors.push(color);
      } else {
        // No explicit color assignment. Use the next from the palette.
        colors.push(palette[nextUnassignedColorIndex]);
        nextUnassignedColorIndex = nextUnassignedColorIndex < palette.length - 1 ? nextUnassignedColorIndex + 1 : 0;
      }
    }

    if (chartFormat.colors.map) {
      switch (chartData.seriesType) {
        case SeriesType.LINE:
        case SeriesType.BUBBLE:
          for (const series of chartData.series) {
            appendColorFor(series.name);
          }
          break;
        case SeriesType.PAIRED_POINT:
          for (const series of chartData.categories) {
            appendColorFor(series);
          }
          break;
        case SeriesType.NAMED_POINT:
          for (const series of chartData.series) {
            for (const point of series.data) {
              appendColorFor(point.name);
            }
          }
          break;
      }
    } else {
      console.error('By the time we get to formatColors(), the series-to-color map should be backfilled');
      colors = renderOptions.palette.slice();
    }

    return { ...chartOptions, colors };
  }

  /**
   * Legends
   */

  export function formatLegend(
    model: RenderDataModel,
    encoding: Encoding,
    chartOptions: Partial<Highcharts.Options>,
    chartData: ChartData,
    chartFormat: ChartTypes.ChartFormat,
    renderOptions?: ChartRenderOptions
  ): Highcharts.Options {
    let legendPositionOptions = {};
    switch (chartFormat.legend.position) {
      case 'right':
        legendPositionOptions = {
          legend: {
            align: 'right',
            verticalAlign: 'top',
            layout: 'vertical',
          },
        };
        break;
      case 'top':
        legendPositionOptions = {
          legend: {
            align: 'right',
            verticalAlign: 'top',
            layout: 'horizontal',
          },
        };
        break;
      case 'left':
        legendPositionOptions = {
          legend: {
            align: 'left',
            verticalAlign: 'top',
            layout: 'vertical',
          },
        };
        break;
      case 'bottom':
        legendPositionOptions = {
          legend: {
            align: 'center',
            verticalAlign: 'bottom',
            layout: 'horizontal',
          },
        };
        break;
      case 'auto':
        if (chartData.series.length === 1 && renderOptions.containerHeight > 200) {
          legendPositionOptions = {
            legend: {
              align: 'right',
              verticalAlign: 'top',
              layout: 'horizontal',
            },
          };
        } else {
          legendPositionOptions = {
            legend: {
              align: 'right',
              verticalAlign: 'top',
              layout: 'vertical',
            },
          };
        }
        break;
    }

    return merge(chartOptions, legendPositionOptions, {
      legend: {
        enabled: chartFormat.legend.enabled,

        title: {
          enabled: chartFormat.legend.titleEnabled,
        },

        itemMarginBottom: 4,
        itemMarginTop: 1,
        labelFormatter() {
          // This is having to circumvent the fact that highcharts internals is defaulting the value of this.name
          // to strings like "Series 1" when given a null value. In order for us to actually show the string 'null'
          // we need to go all the way back to the source and ensure that the value was actually null, and in that case
          // show the string null.
          if (this.chart.options.series[this.index].name === null) {
            return 'null';
          }
          return this.name;
        },
      },
    });
  }

  export function formatPieLegend(
    model: RenderDataModel,
    encoding: Encoding,
    chartOptions: Partial<Highcharts.Options>,
    chartData: ChartData,
    chartFormat: ChartTypes.ChartFormat,
    renderOptions?: ChartRenderOptions
  ): Highcharts.Options {
    let legendPositionOptions = {};
    switch (chartFormat.legend.position) {
      case 'right':
        legendPositionOptions = {
          legend: {
            align: 'right',
            verticalAlign: 'middle',
            layout: 'vertical',
          },
        };
        break;
      case 'top':
        legendPositionOptions = {
          legend: {
            align: 'center',
            verticalAlign: 'top',
            layout: 'horizontal',
          },
        };
        break;
      case 'left':
        legendPositionOptions = {
          legend: {
            align: 'left',
            verticalAlign: 'middle',
            layout: 'vertical',
          },
        };
        break;
      case 'auto':
      case 'bottom':
        legendPositionOptions = {
          legend: {
            align: 'center',
            verticalAlign: 'bottom',
            layout: 'horizontal',
          },
        };
        break;
    }

    return merge(chartOptions, legendPositionOptions, {
      legend: {
        enabled: chartFormat.legend.enabled,

        labelFormatter: function () {
          return this.options.name == null ? 'null' : this.options.name;
        },

        title: {
          enabled: chartFormat.legend.titleEnabled,
        },

        itemMarginBottom: 4,
        itemMarginTop: 1,
      },
    });
  }

  export function formatPieSize(
    model: RenderDataModel,
    encoding: Encoding,
    chartOptions: Partial<Highcharts.Options>,
    chartData: ChartData,
    chartFormat: ChartTypes.ChartFormat,
    renderOptions?: ChartRenderOptions
  ): Highcharts.Options {
    let pieSizeOptions = {};

    const sizeMappings = [
      { height: 0, size: '116px' },
      { height: 216, size: '95%' },
      { height: 347, size: '90%' },
      { height: 450, size: '85%' },
      { height: 550, size: '75%' },
      { height: 600, size: '70%' },
      { height: 700, size: '65%' },
      { height: 800, size: '60%' },
    ];

    const pieSizeRules = sizeMappings.map((dimension) => ({
      condition: { minHeight: dimension.height },
      chartOptions: {
        plotOptions: {
          pie: { size: dimension.size },
        },
      },
    }));

    if (renderOptions.size) {
      pieSizeOptions = {
        responsive: {
          rules: pieSizeRules,
        },
      };
    }

    return merge(chartOptions, pieSizeOptions);
  }

  /**
   * Data Labels (Bar Values, Pie Chart Values, Donut Chart Values)
   */

  export function formatPlotSeriesDataLabels(
    model: RenderDataModel,
    encoding: Encoding,
    chartOptions: Partial<Highcharts.Options>,
    chartData: ChartData,
    chartFormat: ChartTypes.ChartFormat,
    renderOptions?: ChartRenderOptions
  ): Highcharts.Options {
    const valueCallback = function (point) {
      return point.y;
    };

    return merge(chartOptions, {
      plotOptions: {
        series: {
          dataLabels: {
            enabled: chartFormat.showValues,

            formatter: makeNumberFormatter(
              chartFormat.yAxis.numberFormat,
              valueCallback,
              makeDefaultLabelCallback(valueCallback)
            ),

            style: {
              color: 'black',
              fontSize: '11px',
              fontWeight: '300',
              textOutline: '1px contrast',
            },
          },
        },
      },
    });
  }

  /**
   * Tooltips
   */

  export function formatSharedTooltips(
    model: RenderDataModel,
    encoding: Encoding,
    chartOptions: Partial<Highcharts.Options>,
    chartData: ChartData,
    chartFormat: ChartTypes.ChartFormat,
    renderOptions?: ChartRenderOptions
  ): Highcharts.Options {
    return merge(chartOptions, {
      tooltip: {
        shared: true,
        hideDelay: 0,
      },
    });
  }

  export function formatIndividualTooltipContent(
    model: RenderDataModel,
    encoding: Encoding,
    chartOptions: Partial<Highcharts.Options>,
    chartData: ChartData,
    chartFormat: ChartTypes.ChartFormat,
    renderOptions?: ChartRenderOptions
  ): Highcharts.Options {
    const valueCallback = function (point) {
      return point.y;
    };

    const yAxisFormatter = makeNumberFormatter(
      chartFormat.yAxis.numberFormat,
      valueCallback,
      makeDefaultLabelCallback(valueCallback)
    );

    const y2AxisFormatter = makeNumberFormatter(
      chartFormat.y2Axis.numberFormat,
      valueCallback,
      makeDefaultLabelCallback(valueCallback)
    );

    let seriesEnc: EncField;
    if (encoding.x[0].formula !== Names.Key) {
      seriesEnc = encoding.x[0];
    } else {
      seriesEnc = encoding.column[0];
    }

    const seriesField = model.compileEncField(seriesEnc);
    let seriesValueFormatter;

    return merge(chartOptions, {
      tooltip: {
        hideDelay: 0,
        useHTML: true,
        padding: 0,
        formatter: function () {
          let headerText;

          switch (seriesField.formulaDataType) {
            case DataType.Name.Decimal:
            case DataType.Name.Integer:
            case DataType.Name.Float:
              if (seriesField.hasDatePart && seriesField.isDiscrete) {
                seriesValueFormatter = makeSeriesNameFormatter(seriesField, chartFormat);
                headerText = seriesValueFormatter(this.key);
              } else {
                seriesValueFormatter = formatNumberWithPrecision(this.x, chartFormat.xAxis.numberFormat.precision);
                headerText = seriesValueFormatter;
              }
              break;
            default:
              seriesValueFormatter = makeSeriesNameFormatter(seriesField, chartFormat);
              headerText = seriesValueFormatter(this.key);
          }
          // The below || 'null' is ensuring that the underlying data is not affected,
          // while still allowing to properly display the 'null' within the tooltip
          const header = `
            <div class="tooltip-header">
              ${escape(headerText || 'null')}
            </div>
            <div class="tooltip-body no-total">
          `;

          // The below usage of this.series.userOptions.name is used because when we were using this.series.name
          // highcharts internally was defaulting null values to 'Series (index + 1)'
          const point = `
            <div class="tooltip-row">
              <div class="tooltip-row-item legend single-row">
                <span class="legend-icon" style="color:${this.point.color};">\u25CF</span>
                <span>${escape(this.series.userOptions.name || 'null')}</span>
              </div>
              <div class="tooltip-row-item tooltip-label non-area-label">
                <span class="label-text">${escape(yAxisFormatter.call(this.point))}</span>
              </div>
            </div>
          `;

          const footer = `</div>`;

          return '<div class="tooltip-container">' + header + point + footer + '</div>';
        },
      },
    });
  }

  export function formatSharedTooltipContent(
    model: RenderDataModel,
    encoding: Encoding,
    chartOptions: Partial<Highcharts.Options>,
    chartData: ChartData,
    chartFormat: ChartTypes.ChartFormat,
    renderOptions?: ChartRenderOptions
  ): Highcharts.Options {
    const valueCallback = function (point) {
      return point.y;
    };

    const yAxisFormatter = makeNumberFormatter(
      chartFormat.yAxis.numberFormat,
      valueCallback,
      makeDefaultLabelCallback(valueCallback)
    );

    const y2AxisFormatter = makeNumberFormatter(
      chartFormat.y2Axis.numberFormat,
      valueCallback,
      makeDefaultLabelCallback(valueCallback)
    );

    const xEnc = encoding.x[0];
    const xField = model.compileEncField(xEnc);
    let xValueFormatter;

    return merge(chartOptions, {
      tooltip: {
        hideDelay: 0,
        useHTML: true,
        padding: 0,
        formatter: function (event: HighchartsEvents.TooltipEvent): string {
          const { hoverPoint } = event.chart;
          let headerText;

          switch (xField.formulaDataType) {
            case DataType.Name.Decimal:
            case DataType.Name.Integer:
            case DataType.Name.Float:
              if (xField.hasDatePart && xField.isDiscrete) {
                xValueFormatter = makeSeriesNameFormatter(xField, chartFormat);
                headerText = xValueFormatter(this.x);
              } else {
                xValueFormatter = formatNumberWithPrecision(this.x, chartFormat.xAxis.numberFormat.precision);
                headerText = xValueFormatter;
              }
              break;

            default:
              xValueFormatter = makeSeriesNameFormatter(xField, chartFormat);
              headerText = xValueFormatter(this.x);
          }

          const header = `
            <div class="tooltip-header">
              ${this.x == null ? 'null' : escape(headerText)}
            </div>
            <div class="tooltip-body no-total tooltip-body--column-flow">
          `;

          const total = 0;
          const pointsNames = new Array<string>();
          const pointsValues = new Array<string>();
          for (const point_i of this.points) {
            const isHighlighted = validateTooltipHighlighting(hoverPoint, point_i, this.points.length);

            let labelValue: string;

            const point = point_i.point;
            const series = point_i.series;

            switch (point_i.series.yAxis.opposite) {
              case true:
                labelValue = y2AxisFormatter.call(point);
                break;
              default:
                labelValue = yAxisFormatter.call(point);
                break;
            }

            pointsNames.push(`
              <div class="tooltip-column-name ${isHighlighted}">
                <span class="legend-icon" style="color:${point.color};">\u25CF</span>
                <span>${escape(series.userOptions.name) || 'null'}</span>
              </div>
            `);

            pointsValues.push(`
              <div class="tooltip-column-value ${isHighlighted}">
                <span class="label-text">${escape(labelValue)}</span>
              </div>
            `);
          }

          const footer = `</div>`;

          return (
            '<div class="tooltip-container">' +
            header +
            `<div class="tooltip-column tooltip-column--names">${pointsNames.join('')}</div>` +
            `<div class="tooltip-column tooltip-column--values">${pointsValues.join('')}</div>` +
            footer +
            '</div>'
          );
        },
      },
    });
  }

  export function formatAreaTooltipContent(
    model: RenderDataModel,
    encoding: Encoding,
    chartOptions: Partial<Highcharts.Options>,
    chartData: ChartData,
    chartFormat: ChartTypes.ChartFormat,
    renderOptions?: ChartRenderOptions
  ): Highcharts.Options {
    const valueCallback = function (point) {
      return point.y;
    };
    const totalValueCallback = function (value) {
      return value;
    };

    const yAxisFormatter = makeNumberFormatter(
      chartFormat.yAxis.numberFormat,
      valueCallback,
      makeDefaultLabelCallback(valueCallback)
    );

    const yAxisTotalFormatter = makeNumberFormatter(
      chartFormat.yAxis.numberFormat,
      totalValueCallback,
      makeDefaultLabelCallback(totalValueCallback)
    );

    const xEnc = encoding.x[0];
    const xField = model.compileEncField(xEnc);
    let xValueFormatter;

    return merge(chartOptions, {
      tooltip: {
        hideDelay: 0,
        useHTML: true,
        padding: 0,
        formatter: function (event: HighchartsEvents.TooltipEvent): string {
          const { hoverPoint } = event.chart;
          let headerText;

          switch (xField.formulaDataType) {
            case DataType.Name.Decimal:
            case DataType.Name.Integer:
            case DataType.Name.Float:
              if (xField.hasDatePart && xField.isDiscrete) {
                xValueFormatter = makeSeriesNameFormatter(xField, chartFormat);
                headerText = xValueFormatter(this.x);
              } else {
                xValueFormatter = formatNumberWithPrecision(this.x, chartFormat.xAxis.numberFormat.precision);
                headerText = xValueFormatter;
              }
              break;

            default:
              xValueFormatter = makeSeriesNameFormatter(xField, chartFormat);
              headerText = xValueFormatter(this.x);
          }

          const header = `
            <div class="tooltip-header">
              ${this.x == null ? 'null' : escape(headerText)}
            </div>
            <div class="tooltip-body tooltip-body--column-flow">
          `;

          let total = 0;
          const pointsNames = new Array<string>();
          const pointsValues = new Array<string>();
          for (const point_i of this.points) {
            let labelValue: string;
            const isHighlighted = validateTooltipHighlighting(hoverPoint, point_i, this.points.length);

            const point = point_i.point;
            const series = point_i.series;

            total += point.y;
            labelValue = yAxisFormatter.call(point);

            pointsNames.push(`
              <div class="tooltip-column-name ${isHighlighted}">
                <span class="legend-icon" style="color:${point.color};">\u25CF</span>
                <span>${escape(series.userOptions.name) || 'null'}</span>
              </div>
            `);

            pointsValues.push(`
              <div class="tooltip-column-value ${isHighlighted}">
                <span class="label-text">${escape(labelValue)}</span>
              </div>
            `);
          }

          const footer = `
            </div>
            <div class="tooltip-footer">
              <div class="footer-segment footer-total">
                <span class="legend-icon">\u25CF</span>
                <span class="footer-text">Total</span>
              </div>
              <div class="footer-segment footer-value">
                <span class="footer-text">${yAxisTotalFormatter.call(total)}</span>
              </div>
            </div>
          `;

          return (
            '<div class="tooltip-container">' +
            header +
            `<div class="tooltip-column tooltip-column--names">${pointsNames.join('')}</div>` +
            `<div class="tooltip-column tooltip-column--values">${pointsValues.join('')}</div>` +
            footer +
            '</div>'
          );
        },
      },
    });
  }

  export function formatTotalAreaTooltipContent(
    model: RenderDataModel,
    encoding: Encoding,
    chartOptions: Partial<Highcharts.Options>,
    chartData: ChartData,
    chartFormat: ChartTypes.ChartFormat,
    renderOptions?: ChartRenderOptions
  ): Highcharts.Options {
    const valueCallback = function (point) {
      return point.y;
    };
    const totalValueCallback = function (value) {
      return value;
    };
    const percentValueCallback = function (point) {
      return point.percentage / 100.0;
    };

    const yAxisPercentFormat: ChartTypes.ChartNumberFormat = {
      format: ChartTypes.NumberFormatTypes.PERCENT,
      precision: chartFormat.yAxis.numberFormat.precision || 2,
    };

    const yAxisFormatter = makeNumberFormatter(
      chartFormat.yAxis.numberFormat,
      valueCallback,
      makeDefaultLabelCallback(valueCallback)
    );

    const yAxisTotalFormatter = makeNumberFormatter(
      chartFormat.yAxis.numberFormat,
      totalValueCallback,
      makeDefaultLabelCallback(totalValueCallback)
    );

    const yAxisPercentFormatter = makeNumberFormatter(
      yAxisPercentFormat,
      percentValueCallback,
      makeDefaultLabelCallback(percentValueCallback)
    );

    const xEnc = encoding.x[0];
    const xField = model.compileEncField(xEnc);
    let xValueFormatter;

    return merge(chartOptions, {
      tooltip: {
        hideDelay: 0,
        useHTML: true,
        padding: 0,
        formatter: function (event: HighchartsEvents.TooltipEvent): string {
          const { hoverPoint } = event.chart;

          let headerText;

          switch (xField.formulaDataType) {
            case DataType.Name.Decimal:
            case DataType.Name.Integer:
            case DataType.Name.Float:
              if (xField.hasDatePart && xField.isDiscrete) {
                xValueFormatter = makeSeriesNameFormatter(xField, chartFormat);
                headerText = xValueFormatter(this.x);
              } else {
                xValueFormatter = formatNumberWithPrecision(this.x, chartFormat.xAxis.numberFormat.precision);
                headerText = xValueFormatter;
              }
              break;

            default:
              xValueFormatter = makeSeriesNameFormatter(xField, chartFormat);
              headerText = xValueFormatter(this.x);
          }

          $('.highcharts-tooltip').show();

          const header = `
            <div class="tooltip-header">
              ${this.x == null ? 'null' : escape(headerText)}
            </div>
            <div class="tooltip-body header tooltip-body--column-flow">
          `;

          let total = 0;
          const pointsNames = new Array<string>();
          const pointsValues = new Array<string>();
          const pointsPercents = new Array<string>();
          for (const point_i of this.points) {
            let labelValue: string;
            let percentLabelValue: string;
            const isHighlighted = validateTooltipHighlighting(hoverPoint, point_i, this.points.length);

            const point = point_i.point;
            const series = point_i.series;
            total += point.y;
            labelValue = yAxisFormatter.call(point);
            percentLabelValue = yAxisPercentFormatter.call(point);

            pointsNames.push(`
              <div class="tooltip-column-name ${isHighlighted}">
                <span class="legend-icon" style="color:${point.color};">\u25CF</span>
                <span>${escape(series.userOptions.name) || 'null'}</span>
              </div>
            `);

            pointsValues.push(`
              <div class="tooltip-column-value ${isHighlighted}">
                <span class="label-text">${escape(labelValue)}</span>
              </div>
            `);

            pointsPercents.push(`
              <div class="tooltip-column-percent ${isHighlighted}">
                <span class="percent-label" title="${percentLabelValue}">${percentLabelValue}</span>
              </div>
            `);
          }

          // since we're using flex to define these columns,
          // in order to have columns of the same width this
          // allows us to grab the largest percent value
          // (positive or negative ex. -98% > 5%) and
          // add it as a transparent column to the footer
          // much like the icon is added transparently for
          // alignment purposes
          const largestPointPercentage = this.points.reduce((acc, curr) => {
            if (Math.abs(curr.point.percentage) > Math.abs(acc.point.percentage)) return curr;
            return acc;
          });

          const footer = `
            </div>
            <div class="tooltip-footer">
              <div class="footer-segment footer-total">
                <span class="legend-icon">\u25CF</span>
                <span class="footer-text">Total</span>
              </div>
              <div class="footer-segment footer-value">
                <span class="footer-text">${yAxisTotalFormatter.call(total)}</span>
              </div>
              <div class="footer-segment footer-percent">
                <span>${yAxisPercentFormatter.call(largestPointPercentage.point)}</span>
            </div>
          `;

          return (
            '<div class="tooltip-container">' +
            header +
            `<div class="tooltip-column tooltip-column--names">${pointsNames.join('')}</div>` +
            `<div class="tooltip-column tooltip-column--values">${pointsValues.join('')}</div>` +
            `<div class="tooltip-column tooltip-column--percent">${pointsPercents.join('')}</div>` +
            footer +
            '</div>'
          );
        },
      },
    });
  }

  export function formatPieTooltipContent(
    model: RenderDataModel,
    encoding: Encoding,
    chartOptions: Partial<Highcharts.Options>,
    chartData: ChartData,
    chartFormat: ChartTypes.ChartFormat,
    renderOptions?: ChartRenderOptions
  ): Highcharts.Options {
    const valueCallback = function (point) {
      return point.y;
    };

    const yAxisFormat: ChartTypes.ChartNumberFormat = {
      format: null,
      precision: chartFormat.yAxis.numberFormat.precision || 2,
    };

    const yAxisFormatter = makeNumberFormatter(yAxisFormat, valueCallback, makeDefaultLabelCallback(valueCallback));

    const seriesEnc = encoding.marks.properties.color.values[0];
    const seriesField = model.compileEncField(seriesEnc);
    const seriesValueFormatter = makeSeriesNameFormatter(seriesField, chartFormat);

    return merge(chartOptions, {
      tooltip: {
        hideDelay: 0,
        useHTML: true,
        formatter: function () {
          const header = `
            <div class="tooltip-header">
              ${this.point.options.name == null ? 'null' : escape(seriesValueFormatter(this.point.options.name))}
            </div>
            <div class="tooltip-body no-total">
          `;

          const point = `
            <div class="tooltip-row">
              <div class="tooltip-row-item legend single-row">
                <span class="legend-icon" style="color:${this.point.color};">\u25CF</span>
                <span>${escape(this.series.userOptions.name) || 'null'}</span>
              </div>
              <div class="tooltip-row-item tooltip-label non-area-label">
                <span class="label-text">${escape(yAxisFormatter.call(this.point))}</span>
              </div>
            </div>
          `;

          const footer = `</div>`;

          return '<div class="tooltip-container">' + header + point + footer + '</div>';
        },
      },
    });
  }

  export function formatPieDataLabels(
    model: RenderDataModel,
    encoding: Encoding,
    chartOptions: Partial<Highcharts.Options>,
    chartData: ChartData,
    chartFormat: ChartTypes.ChartFormat,
    renderOptions?: ChartRenderOptions
  ): Highcharts.Options {
    const valueCallback = function (point) {
      return point.y;
    };

    return merge(chartOptions, {
      plotOptions: {
        pie: {
          dataLabels: {
            format: chartFormat.pieLabelsNotPercentages ? '{point.name}' : '{percentage:.2f}%',
          },
        },
      },
    });
  }

  /**
   * Plot Options
   */

  export function formatPlotSeries(
    model: RenderDataModel,
    encoding: Encoding,
    chartOptions: Partial<Highcharts.Options>,
    chartData: ChartData,
    chartFormat: ChartTypes.ChartFormat,
    renderOptions?: ChartRenderOptions
  ): Highcharts.Options {
    return merge(chartOptions, {
      plotOptions: merge(chartOptions.plotOptions || {}, {
        series: {
          animation: false,
        },
      }),
    });
  }

  /**
   * Series
   */

  export function formatSeries(
    model: RenderDataModel,
    encoding: Encoding,
    chartOptions: Partial<Highcharts.Options>,
    chartData: ChartData,
    chartFormat: ChartTypes.ChartFormat,
    renderOptions?: ChartRenderOptions
  ): Highcharts.Options {
    return {
      ...chartOptions,
      series: chartData.series as any, // TODO: make this compatible with Highcharts 9 types
    };
  }

  /**
   * Title
   */

  export function formatTitle(
    model: RenderDataModel,
    encoding: Encoding,
    chartOptions: Partial<Highcharts.Options>,
    chartData: ChartData,
    chartFormat: ChartTypes.ChartFormat,
    renderOptions?: ChartRenderOptions
  ): Highcharts.Options {
    return {
      ...chartOptions,
      title: {
        text: null,
      },
    };
  }

  // This function at a high level is responsible for doing 2 things
  // Thing 1: Remove null data points if the chart in question is a "Line" like chart and the field is continuous
  // Thing 2: based on the `renderOptions.showOmittedNullMessaging` we should appropriately add a custom caption detailing to the user that nulls have been omitted from the rendered chart.
  export function formatNullsOutOfContinuousCharts(
    model: RenderDataModel,
    encoding: Encoding,
    chartOptions: Partial<Highcharts.Options>,
    chartData: ChartData,
    chartFormat: ChartTypes.ChartFormat,
    renderOptions?: ChartRenderOptions
  ): Highcharts.Options {
    const encField = encoding.column.length > 0 ? encoding.column[0] : encoding.x[0];
    const xField = model.compileEncField(encField);
    let numOfNullsRemoved = 0;
    const seriesIndexsToBeRemoved = [];

    const renderNullCaption = function ({ fieldName, nullCount }) {
      const nullPluralizedString = nullCount === 1 ? 'NULL' : 'NULLS';
      const maxChars = 25;
      const truncatedFieldName = fieldName.length > maxChars ? fieldName.substr(0, maxChars - 1) + '…' : fieldName;
      const tooltipText = `Change ${truncatedFieldName} from continuous to discrete to visualize null values.`;
      const notificationText = `${nullCount} ${nullPluralizedString} OMITTED`;
      return `
        <div class="null-values-hidden" data-tooltip-text="${tooltipText}">
          <span>${notificationText}</span>
        </div>
      `;
    };

    // process series data here to ensure that we dont embed null values for this continuous series.
    if (xField.varType === Attr.Type.Continuous) {
      chartOptions.series.forEach((series, index) => {
        const seriesOpts = chartOptions.series[index];
        /**
         * Due to more complete type definitions in Highcharts 9, we need to do some
         * runtime checks to keep our code compatible. This should be refactored at
         * some point to use the type discrimination intended by Highcharts
         * */
        if ('data' in seriesOpts && 'data' in series && Array.isArray(series.data)) {
          seriesOpts.data = series.data.filter((seriesValue) => {
            // the following check is checking a couple different places that a null value could reside.
            // for scatter/bubble type charts seriesValue will be an object with x,y,z properties
            // for all bar/line type charts seriesValue will be a tuple where the first value is associated to the x value.
            // @ts-ignore
            if (seriesValue[0] === null || seriesValue.x === null) {
              numOfNullsRemoved++;
              return false;
            }
            return true;
          });
          // The chartOptions.series is what is used to generate the legend items. If we have removed
          // all of the series values due to nulls being omitted, then we need to also remove the series itself
          // otherwise we would still be showing the null within the legend even though we aren't rendering it in the graph.
          if (seriesOpts.data.length === 0) {
            seriesIndexsToBeRemoved.push(index);
          }
        }
      });
      // This will prune out all empty series in the case that an entire series contained null values and
      // we don't intend on rendering that series in the continuous mode.
      seriesIndexsToBeRemoved.forEach((indexValue) => {
        chartOptions.series.splice(indexValue, 1);
      });
      // If the the visualization is being rendered in the designer context, then we need to make sure not to show the
      // null caption information, but we do still need to filter the nulls out correctly.
      if (numOfNullsRemoved > 0 && renderOptions.showOmittedNullMessaging) {
        // @ts-ignore
        chartOptions.caption = {
          text: renderNullCaption({ fieldName: xField.displayName, nullCount: numOfNullsRemoved }),
          useHTML: true,
          align: 'right',
          y: 10,
          style: {
            backgroundColor: '#b1b2ba',
            padding: '0 4px',
            height: 16,
            fontSize: '10px',
            fontWeight: '500',
            borderRadius: 2,
            color: '#ffffff',
          },
        };
      }
    }

    return chartOptions;
  }

  /**
   * X Axis
   */

  export function formatXAxis(
    model: RenderDataModel,
    encoding: Encoding,
    chartOptions: Partial<Highcharts.Options>,
    chartData: ChartData,
    chartFormat: ChartTypes.ChartFormat,
    renderOptions?: ChartRenderOptions
  ): Highcharts.Options {
    const encField = encoding.column.length > 0 ? encoding.column[0] : encoding.x[0];
    const xField = model.compileEncField(encField);

    let xAxisType = 'category';
    if (xField.isValid) {
      if (xField.isContinuous) {
        // If it's a DATETRUNC or a DateTime ACCESS, need to use datetime axis for highcharts to render right
        if (xField.hasDateTrunc || xField.isFormulaDate) {
          xAxisType = 'datetime';
        } else {
          xAxisType = 'linear';
        }
      }
    }

    let xAxisCategories = [];
    switch (chartData.seriesType) {
      case SeriesType.LINE:
        switch (xField.varType) {
          case Attr.Type.Discrete:
            xAxisCategories = chartData.categories;
            break;
          case Attr.Type.Continuous:
            xAxisCategories = undefined;
            break;
        }
        break;
      case SeriesType.BUBBLE:
        xAxisCategories = chartData.categories;
        break;
    }

    let xAxes: Highcharts.AxisOptions[];
    if (isArray(chartOptions.xAxis)) {
      xAxes = chartOptions.xAxis;
    } else {
      xAxes = [chartOptions.xAxis];
    }

    return merge(chartOptions, {
      xAxis: xAxes.map((axis) =>
        merge(axis, {
          type: xAxisType,
          categories: xAxisCategories,
          className: xAxisCategories && xAxisCategories.includes(null) ? 'has-null-category' : null, // styles null in italics

          title: merge(axis.title, {
            margin: 10,
          }),

          labels: formatXAxisLabels(model, encoding, xField, chartOptions, chartData, chartFormat, renderOptions),
        })
      ),
    });
  }

  export function formatScatterXAxisRange(
    model: RenderDataModel,
    encoding: Encoding,
    chartOptions: Partial<Highcharts.Options>,
    chartData: ChartData,
    chartFormat: ChartTypes.ChartFormat,
    renderOptions?: ChartRenderOptions
  ): Highcharts.Options {
    let xAxes: Highcharts.AxisOptions[];
    if (isArray(chartOptions.xAxis)) {
      xAxes = chartOptions.xAxis;
    } else {
      xAxes = [chartOptions.xAxis];
    }

    return merge(chartOptions, {
      xAxis: xAxes.map((axis) =>
        merge(axis, {
          min: chartFormat.xAxis.numberRange.min,
          max: chartFormat.xAxis.numberRange.max,
        })
      ),
    });
  }

  export function formatStackedBarXAxisPadding(
    model: RenderDataModel,
    encoding: Encoding,
    chartOptions: Partial<Highcharts.Options>,
    chartData: ChartData,
    chartFormat: ChartTypes.ChartFormat,
    renderOptions?: ChartRenderOptions
  ): Highcharts.Options {
    const xEncField = encoding.x[0];
    const cEncField = encoding.marks.series[0].properties.color.values[0];

    let xAxes: Highcharts.AxisOptions[];
    if (isArray(chartOptions.xAxis)) {
      xAxes = chartOptions.xAxis;
    } else {
      xAxes = [chartOptions.xAxis];
    }

    return merge(chartOptions, {
      xAxis: xAxes.map((axis) => {
        if (
          xEncField.variable.type === VariableType.CONTINUOUS &&
          cEncField != null &&
          cEncField.formula !== Names.Key
        ) {
          return merge(axis, {
            minPadding: 0.03,
            maxPadding: 0.03,
          });
        } else {
          return axis;
        }
      }),
    });
  }

  function formatXAxisLabels(
    model: RenderDataModel,
    encoding: Encoding,
    xField: CompiledFormula,
    chartOptions: Partial<Highcharts.Options>,
    chartData: ChartData,
    chartFormat: ChartTypes.ChartFormat,
    renderOptions?: ChartRenderOptions
  ): Highcharts.XAxisLabelsOptions {
    const axisLabelOptions = {
      rotation: chartFormat.xAxis.rotate,
    };

    if (!xField.isValid) {
      return axisLabelOptions;
    }

    if (xField.hasDateFunc || xField.isFormulaDate) {
      if (xField.hasDatePart) {
        /**
         * DateParts come preformatted via makeXValueFormatter, so we pass to the string formatter.
         * It's possibly inconsistent to do this, but most likely a lot of the system was designed around
         * passing around formatted dateparts instead of the raw number.
         */
        return merge(axisLabelOptions, {
          formatter: makeStringFormatter(),
        });
      } else {
        // Date truncs or ACCESS come in a millisecond timestamps, and must be formatted
        return merge(axisLabelOptions, {
          formatter: makeDateFormatter(
            ChartDate.getDatePart(xField),
            chartFormat.xAxis.dateFormat,
            function (point: any) {
              return point.value;
            },
            true,
            xField.yearStart ?? xField.defaultYearStart
          ),
        });
      }
    }

    switch (xField.formulaDataType) {
      case DataType.Name.String:
        return merge(axisLabelOptions, {
          formatter: makeStringFormatter(),
        });
      case DataType.Name.Float:
      case DataType.Name.Integer:
      case DataType.Name.Decimal:
        if (chartFormat.xAxis.numberFormat.format === 'none') {
          return merge(axisLabelOptions, {
            formatter: makeStringFormatter(),
          });
        } else {
          return merge(axisLabelOptions, {
            formatter: makeNumberFormatter(
              chartFormat.xAxis.numberFormat,
              function (point) {
                return point.value;
              },
              function (point) {
                return point.axis.defaultLabelFormatter.call(point);
              }
            ),
          });
        }
      default:
        return axisLabelOptions;
    }
  }

  /**
   * Y Axis
   */

  export function formatYAxis(
    model: RenderDataModel,
    encoding: Encoding,
    chartOptions: Partial<Highcharts.Options>,
    chartData: ChartData,
    chartFormat: ChartTypes.ChartFormat,
    renderOptions?: ChartRenderOptions
  ): Highcharts.Options {
    let yAxes: Highcharts.AxisOptions[];
    if (isArray(chartOptions.yAxis)) {
      yAxes = chartOptions.yAxis;
    } else {
      yAxes = [chartOptions.yAxis];
    }

    return merge(chartOptions, {
      yAxis: yAxes.map((axis) =>
        merge(axis, {
          type: chartFormat.yAxis.scaleType,

          gridLineColor: '#f1f2f5',

          labels: {
            formatter: makeAxisLabelFormatter(chartFormat.yAxis.numberFormat),
          },

          min: chartFormat.yAxis.numberRange.min,
          max: chartFormat.yAxis.numberRange.max,
          tickPositioner: checkYAxisMax(chartFormat),
        })
      ),
    });
  }

  export function formatTotalAreaYAxis(
    model: RenderDataModel,
    encoding: Encoding,
    chartOptions: Partial<Highcharts.Options>,
    chartData: ChartData,
    chartFormat: ChartTypes.ChartFormat,
    renderOptions?: ChartRenderOptions
  ): Highcharts.Options {
    let yAxes: Highcharts.AxisOptions[];
    if (isArray(chartOptions.yAxis)) {
      yAxes = chartOptions.yAxis;
    } else {
      yAxes = [chartOptions.yAxis];
    }

    return merge(chartOptions, {
      yAxis: yAxes.map((axis) =>
        merge(axis, {
          title: merge(axis.title, {
            margin: 20,
          }),

          labels: {
            format: '{value}%',
          },
        })
      ),
    });
  }

  export function formatStackedYAxis(
    model: RenderDataModel,
    encoding: Encoding,
    chartOptions: Partial<Highcharts.Options>,
    chartData: ChartData,
    chartFormat: ChartTypes.ChartFormat,
    renderOptions?: ChartRenderOptions
  ): Highcharts.Options {
    const valueCallback = function (point) {
      return point.total;
    };

    let yAxes: Highcharts.AxisOptions[];
    if (isArray(chartOptions.yAxis)) {
      yAxes = chartOptions.yAxis;
    } else {
      yAxes = [chartOptions.yAxis];
    }

    return merge(chartOptions, {
      yAxis: yAxes.map((axis) =>
        merge(axis, {
          title: merge(axis.title, {
            margin: 20,
          }),

          labels: {
            formatter: makeAxisLabelFormatter(chartFormat.yAxis.numberFormat),
          },

          stackLabels: {
            enabled: chartFormat.showValues,

            formatter: makeNumberFormatter(
              chartFormat.yAxis.numberFormat,
              valueCallback,
              makeDefaultLabelCallback(valueCallback)
            ),

            style: {
              color: 'black',
              fontSize: '11px',
              fontWeight: '300',
              textOutline: '1px contrast',
            },
          },

          type: chartFormat.yAxis.scaleType,
          min: chartFormat.yAxis.numberRange.min,
          max: chartFormat.yAxis.numberRange.max,
          tickPositioner: checkYAxisMax(chartFormat),
        })
      ),
    });
  }

  export function formatStacked100YAxis(
    model: RenderDataModel,
    encoding: Encoding,
    chartOptions: Partial<Highcharts.Options>,
    chartData: ChartData,
    chartFormat: ChartTypes.ChartFormat,
    renderOptions?: ChartRenderOptions
  ): Highcharts.Options {
    const valueCallback = function (point) {
      return point.total;
    };

    let yAxes: Highcharts.AxisOptions[];
    if (isArray(chartOptions.yAxis)) {
      yAxes = chartOptions.yAxis;
    } else {
      yAxes = [chartOptions.yAxis];
    }

    return merge(chartOptions, {
      yAxis: yAxes.map((axis) =>
        merge(axis, {
          title: merge(axis.title, {
            margin: 20,
          }),

          labels: {
            format: '{value}%',
          },

          stackLabels: {
            enabled: chartFormat.showValues,

            formatter: makeNumberFormatter(
              chartFormat.yAxis.numberFormat,
              valueCallback,
              makeDefaultLabelCallback(valueCallback)
            ),

            style: {
              color: 'black',
              fontSize: '11px',
              fontWeight: '300',
              textOutline: '1px contrast',
            },
          },

          type: chartFormat.yAxis.scaleType,
          min: chartFormat.yAxis.numberRange.min,
          max: chartFormat.yAxis.numberRange.max,
        })
      ),
    });
  }

  export function formatComboYAxis(
    model: RenderDataModel,
    encoding: Encoding,
    chartOptions: Partial<Highcharts.Options>,
    chartData: ChartData,
    chartFormat: ChartTypes.ChartFormat,
    renderOptions?: ChartRenderOptions
  ): Highcharts.Options {
    return merge(chartOptions, {
      yAxis: encoding.marks.series.map((series, index) => {
        if (index === 0) {
          return {
            title: {
              margin: 20,
            },

            labels: {
              formatter: makeAxisLabelFormatter(chartFormat.yAxis.numberFormat),
            },

            type: chartFormat.yAxis.scaleType,
            min: chartFormat.yAxis.numberRange.min,
            max: chartFormat.yAxis.numberRange.max,
          };
        } else {
          return {
            opposite: true,

            title: {
              margin: 20,
              rotation: 270,
            },

            labels: {
              formatter: makeAxisLabelFormatter(chartFormat.y2Axis.numberFormat),
            },

            type: chartFormat.y2Axis.scaleType,
            min: chartFormat.y2Axis.numberRange.min,
            max: chartFormat.y2Axis.numberRange.max,
          };
        }
      }),
    });
  }

  /**
   * Helpers / Utilities
   */

  function formatNumberWithoutPrecision(value: number): string {
    return numeral(isNaN(value) ? null : value).format('0,0[.][0000]');
  }

  function formatNumberWithPrecision(value: number, precision: number): string {
    if (precision === null) {
      return formatNumberWithoutPrecision(value);
    }
    const precisionFormat = (): string => {
      let result;
      for (let i = 0; i < precision; i++) {
        result = !result ? '.0' : result + '0';
      }
      return result;
    };

    return numeral(value).format('0,0' + precisionFormat());
  }

  function scaleNumberValue(value: number, format: string): number {
    if (format === 'percent') {
      return value * 100;
    } else {
      const scales = { thousands: 1000, millions: 1000000, billions: 1000000000 };
      return scales[format] ? value / scales[format] : value;
    }
  }

  function makeDefaultLabelCallback(valueCallback: (point: any) => number) {
    return function (point: any) {
      return formatNumberWithoutPrecision(valueCallback(point));
    };
  }

  function makeNumberFormatter(
    numberFormat: ChartTypes.ChartNumberFormat,
    valueCallback: (point: any) => number,
    labelCallback: (point: any) => string
  ): () => string {
    return function (): string {
      let labelValue = labelCallback(this);
      const scaledValue = scaleNumberValue(valueCallback(this), numberFormat.format);

      let formattedValue: string;
      if (numberFormat.precision == null) {
        formattedValue = formatNumberWithoutPrecision(scaledValue);
      } else {
        formattedValue = formatNumberWithPrecision(scaledValue, numberFormat.precision);
      }

      switch (numberFormat.format) {
        case ChartTypes.NumberFormatTypes.PERCENT:
          return `${formattedValue}%`;
        case ChartTypes.NumberFormatTypes.CURRENCY: {
          let negativeSign = '';
          if (numberFormat.precision != null) {
            if (formattedValue[0] === '-') {
              negativeSign = '-';
              formattedValue = trim(formattedValue, '-');
            }
            return `${negativeSign}$${formattedValue}`;
          } else {
            if (labelValue[0] === '-') {
              negativeSign = '-';
              labelValue = trim(labelValue, '-');
            }
            return `${negativeSign}$${labelValue}`;
          }
        }
        case ChartTypes.NumberFormatTypes.THOUSANDS:
          return `${formattedValue}K`;
        case ChartTypes.NumberFormatTypes.MILLIONS:
          return `${formattedValue}M`;
        case ChartTypes.NumberFormatTypes.BILLIONS:
          return `${formattedValue}B`;
        default:
          return formattedValue;
      }
    };
  }

  export function makeDateFormatter(
    datePart: DatePart,
    dateFormat: ChartTypes.DateFormat,
    valueCallback: (point: any) => string | number,
    isXAxisLabels?: boolean,
    yearStart?: DateTime.YearStart
  ): () => string {
    return function (): string {
      return ChartDate.makeFormattedTimestamp(datePart, dateFormat, valueCallback(this), isXAxisLabels, yearStart);
    };
  }

  // This method will detect a null value by checking that the value of the label
  // is ofType string for DataType.Name.String, if its not then we need to show the string "null" otherwise let the
  // value persist as normal.
  function makeStringFormatter(): () => string {
    return function (): string {
      // Highcharts internals will cast null values for specific xAxis types, to 0. This ends up
      // showing date 1-1-1970, and other related bugs. This if statement will look up the chartconfig object
      // and determine if the value was supposed to actually be null.
      if (
        this.axis.categories &&
        this.axis.categories.length > 0 &&
        (this.axis.categories[this.pos] === null || this.axis.categories[this.pos] === undefined)
      ) {
        return this.axis.categories[this.pos] === null ? 'null' : '';
      } else {
        return this.axis.defaultLabelFormatter.call(this);
      }
    };
  }

  function makeAxisLabelFormatter(numberFormat: ChartTypes.ChartNumberFormat): (point: any) => string {
    if (numberFormat.format == null || numberFormat.format === 'none') {
      return makeNumberFormatter(
        numberFormat,
        function (point) {
          return point.value;
        },
        function (point) {
          return point.axis.defaultLabelFormatter.call(point);
        }
      );
    } else {
      return makeNumberFormatter(
        numberFormat,
        function (point) {
          return point.value;
        },
        function (point) {
          return point.axis.defaultLabelFormatter.call(point);
        }
      );
    }
  }

  function validateTooltipHighlighting(
    hoverPoint: HighchartsEvents.HoverPoint,
    markPoint: HighchartsEvents.MarkPoint,
    pointsCount: number
  ): string {
    return hoverPoint.id === markPoint.point.id && pointsCount > 1 ? 'highlight' : '';
  }

  /**
   * checking to see if the max has been set on the y axis
   * if it is, and min is not, we make sure the max actually used on the chart is used
   * doing this by calculating the ticks, and making sure they are divisible by the max number
   * using highcharts tickpositioner option
   * returns a function for tickpositioner
   */
  export function checkYAxisMax(chartFormat: ChartTypes.ChartFormat) {
    if (
      chartFormat.yAxis &&
      chartFormat.yAxis.numberRange &&
      chartFormat.yAxis.numberRange.max !== null &&
      chartFormat.yAxis.numberRange.min === null
    ) {
      return function () {
        const thisYMax = Number(chartFormat.yAxis.numberRange.max);
        if (
          this.chart?.yAxis[0]?.tickPositions &&
          this.chart.yAxis[0].tickPositions.length > 0 &&
          this.chart.yAxis[0].tickPositions[this.chart.yAxis[0].tickPositions.length - 1] !== thisYMax
        ) {
          const numTicks = this.chart.yAxis[0].tickPositions.length;
          return getTickPositions(numTicks, thisYMax);
        }
      };
    }
    return null;
  }
  /**
   * logic that returns the tickpositions, used in tickpositioner option sent to highcharts
   */
  export function getTickPositions(numTicks: number, thisYMax: number): Array<Number> {
    try {
      const positions = [];

      //check to see if that last item in the tick positions array is the same as the wanted max
      //if it is not we do our own math to make sure the last tick position is the max wanted

      let incrementor = 1;
      const incrementorMax = 50; //make sure we don't try and increment a million times..e.g someone uses a prime on the max
      while (thisYMax % (numTicks + incrementor) !== 0 && numTicks + incrementor < incrementorMax) {
        incrementor++;
      }
      //make sure we are not over max allowed tickets for large/prime reasons
      if (numTicks + incrementor < incrementorMax) {
        //with new incrementor to find number of ticks needed
        //we divde to fine the number of tickets we need
        const newTickInterval = thisYMax / (numTicks + incrementor);
        //starting from 0, we basically put the max num at end of tick positions array
        //example: max is 50, numticks was 4, increment is 1
        //unshift(50 - 0 * 10);unshift(50 - 1 * 10);unshift(50 - 2 * 10)...
        //positions array is [0,10,20,30,40,50]
        for (let i = 0; i <= numTicks + incrementor; i++) {
          positions.unshift(thisYMax - i * newTickInterval);
        }
        return positions;
      }
    } catch (e) {
      console.error('tickpositioner error', e);
    }
    return null;
  }
}
